// src/pages/DebtStudio/components/Reports/SourcesUsesReport.jsx
import React from "react";
import { normalizeSourcesUsesBalance } from "pages/DebtStudio/lib/sourcesUsesBalance";

/**
 * SourcesUsesReport
 * --------------------------------------------
 * Displays Sources & Uses of funds for a transaction.
 *
 * • Uses and Sources are aggregated from buildSourcesUses()
 *   which reflects the finalized par amount and computed costs of issuance.
 * • This component does not perform any pricing or date logic.
 * • Supports expandable "Uses" rows with detailed breakdowns
 *   (e.g., COI vendor listings).
 */

/** ---------- Formatters ---------- */
const fmtUSD = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** ---------- Label Normalization ---------- */
/**
 * Normalize labels for readability and apply
 * “Plus/Less” conventions for premium/discount.
 */
function normalizeLabel(label) {
  if (!label) return "—";
  let clean = String(label).trim();

  // Strip parentheticals like (OIP) or (OID)
  clean = clean.replace(/\(.*?\)/g, "").trim();
  const lower = clean.toLowerCase();

  if (
    lower === "oip" ||
    lower.includes("original issue premium") ||
    lower === "premium"
  ) {
    return "Plus: Original Issue Premium";
  }
  if (
    lower === "oid" ||
    lower.includes("original issue discount") ||
    lower === "discount"
  ) {
    return "Less: Original Issue Discount";
  }
  if (lower.includes("rounding")) {
    return "Miscellaneous (Rounding)";
  }

  return clean;
}

/**
 * Build "Type – Firm" label (omits dash if firm missing)
 */
function childLabel(type, firm) {
  const t = normalizeLabel(type || "");
  const f = (firm || "").trim();
  if (t && f) return `${t} – ${f}`;
  return t || f || "—";
}

/** The parent already displays the type for grouped COI detail rows. */
function groupedChildLabel(type, firm) {
  const f = (firm || "").trim();
  return f || normalizeLabel(type || "");
}

/** ---------- Expandable Uses Row ---------- */
function UseRow({ row, expanded, onToggle }) {
  const childCount = Array.isArray(row?.children) ? row.children.length : 0;
  const hasExpandableChildren = childCount > 1;

  // Simple single-child row
  if (childCount === 1) {
    const only = row.children[0] || {};
    return (
      <tr>
        <td className="px-2 py-1">
          {childLabel(only.type || row?.label, only.firm)}
        </td>
        <td className="px-2 py-1 text-right">{fmtUSD(only.amount)}</td>
      </tr>
    );
  }

  // Expandable multi-child row
  return (
    <>
      <tr>
        <td className="px-2 py-1">
          {hasExpandableChildren && (
            <button
              onClick={onToggle}
              className="mr-1 text-xs px-1 border rounded hover:bg-app"
              aria-label={expanded ? "Collapse" : "Expand"}
            >
              {expanded ? "−" : "+"}
            </button>
          )}
          {normalizeLabel(row?.label)}
        </td>
        <td className="px-2 py-1 text-right">
          {!expanded && fmtUSD(row?.amount)}
        </td>
      </tr>

      {expanded &&
        hasExpandableChildren &&
        row.children.map((c, i) => (
          <tr key={`${row.label}-child-${i}`}>
            <td className={`px-2 pt-1 pl-6 ${i === row.children.length - 1 ? "pb-2" : "pb-1"}`}>
              ↳ {groupedChildLabel(c?.type || row?.label, c?.firm)}
            </td>
            <td className={`px-2 pt-1 text-right ${i === row.children.length - 1 ? "pb-2" : "pb-1"}`}>
              {fmtUSD(c?.amount)}
            </td>
          </tr>
        ))}
    </>
  );
}

/** ---------- Main Component ---------- */
export default function SourcesUsesReport({
  data,
  expandedCategories = new Set(),
  onToggleCategory,
}) {
  if (!data) return <p className="text-muted">No Sources & Uses available.</p>;

  const { sources = [], uses = [] } = data;

  /** Compute Totals */
  const totalSources = sources.reduce((sum, s) => sum + (Number(s?.amount) || 0), 0);
  const totalUses = uses.reduce((sum, u) => sum + (Number(u?.amount) || 0), 0);
  const diff = normalizeSourcesUsesBalance(totalSources - totalUses);

  /** ---------- Render ---------- */
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {/* ────────────────────────────────
          SOURCES
         ──────────────────────────────── */}
      <div>
        <h3 className="font-semibold mb-2 text-app">Sources</h3>
        <table className="min-w-full text-sm">
          <tbody>
            {sources.map((s, i) => (
              <tr key={`${s?.label || "source"}-${i}`}>
                <td className="px-2 py-1">{normalizeLabel(s?.label)}</td>
                <td className="px-2 py-1 text-right">{fmtUSD(s?.amount)}</td>
              </tr>
            ))}
            <tr className="font-semibold border-t border-[hsl(var(--border))]">
              <td className="px-2 py-1">Total Sources</td>
              <td className="px-2 py-1 text-right">{fmtUSD(totalSources)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ────────────────────────────────
          USES
         ──────────────────────────────── */}
      <div>
        <h3 className="font-semibold mb-2 text-app">Uses</h3>
        <table className="min-w-full text-sm">
          <tbody>
            {uses.map((u, i) => (
              <UseRow
                key={`${u?.label || "use"}-${i}`}
                row={u}
                expanded={expandedCategories.has(u?.label)}
                onToggle={() => onToggleCategory?.(u?.label)}
              />
            ))}
            <tr className="font-semibold border-t border-[hsl(var(--border))]">
              <td className="px-2 py-1">Total Uses</td>
              <td className="px-2 py-1 text-right">{fmtUSD(totalUses)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ────────────────────────────────
          BALANCE SUMMARY
         ──────────────────────────────── */}
      <div className="md:col-span-2 mt-4">
        <div className="p-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] text-sm flex items-center justify-between">
          <span className="text-muted">Balance (Total Sources − Total Uses)</span>
          <span className={diff === 0 ? "" : "font-semibold"}>
            {fmtUSD(diff)} {diff === 0 ? "(balanced)" : diff < 0 ? "(funding shortfall)" : "(excess sources)"}
          </span>
        </div>
      </div>
    </div>
  );
}
